import BillingsOrderForwardBillingsListParams, {
  BillingsOrderForwardBillingsListParamsOrder,
} from "../../../../../../common/services/billings/order-forward-billings/list/billings-order-forward-billings-list-params";
import paginationService from "../../../../../../common/utils/pagination/pagination.service";
import BillingsTaxiTransferredOrderListingFilter, {
  BillingsTaxiTransferredOrderListingConsumerTaxiFilter,
  BillingsTaxiTransferredOrderListingInternalOrderIdFilter,
  BillingsTaxiTransferredOrderListingOrderStartDateFilter,
  BillingsTaxiTransferredOrderListingProducerTaxiFilter,
  BillingsTaxiTransferredOrderListingStatusFilter,
} from "../types/billings-taxi-transferred-order-listing-filter";
import BillingsTaxiTransferredOrderListingFilterType from "../types/billings-taxi-transferred-order-listing-filter-type";
import BillingsTaxiTransferredOrderListingSortKey from "../types/billings-taxi-transferred-order-listing-sort-key";

const createRequestOrder = (
  sortKey: BillingsTaxiTransferredOrderListingSortKey | null,
): BillingsOrderForwardBillingsListParamsOrder | undefined => {
  const options: {
    sortKey: BillingsTaxiTransferredOrderListingSortKey;
    requestOrder: BillingsOrderForwardBillingsListParamsOrder;
  }[] = [
    {
      requestOrder: BillingsOrderForwardBillingsListParamsOrder.DATE_AT_ASC,
      sortKey: BillingsTaxiTransferredOrderListingSortKey.ORDER_START_DATE_ASC,
    },
    {
      requestOrder: BillingsOrderForwardBillingsListParamsOrder.DATE_AT_DESC,
      sortKey: BillingsTaxiTransferredOrderListingSortKey.ORDER_START_DATE_DESC,
    },
    {
      requestOrder: BillingsOrderForwardBillingsListParamsOrder.HUMAN_ID_ASC,
      sortKey: BillingsTaxiTransferredOrderListingSortKey.INTERNAL_ORDER_ID_ASC,
    },
    {
      requestOrder: BillingsOrderForwardBillingsListParamsOrder.HUMAN_ID_DESC,
      sortKey:
        BillingsTaxiTransferredOrderListingSortKey.INTERNAL_ORDER_ID_DESC,
    },
    {
      requestOrder: BillingsOrderForwardBillingsListParamsOrder.DISTANCE_DESC,
      sortKey: BillingsTaxiTransferredOrderListingSortKey.DISTANCE_DESC,
    },
    {
      requestOrder: BillingsOrderForwardBillingsListParamsOrder.DISTANCE_ASC,
      sortKey: BillingsTaxiTransferredOrderListingSortKey.DISTANCE_ASC,
    },
    {
      requestOrder:
        BillingsOrderForwardBillingsListParamsOrder.DISTANCE_RATE_ASC,
      sortKey: BillingsTaxiTransferredOrderListingSortKey.DISTANCE_RATE_ASC,
    },
    {
      requestOrder:
        BillingsOrderForwardBillingsListParamsOrder.DISTANCE_RATE_DESC,
      sortKey: BillingsTaxiTransferredOrderListingSortKey.DISTANCE_RATE_DESC,
    },
    {
      requestOrder:
        BillingsOrderForwardBillingsListParamsOrder.DISTANCE_AMOUNT_ASC,
      sortKey: BillingsTaxiTransferredOrderListingSortKey.DISTANCE_COST_ASC,
    },
    {
      requestOrder:
        BillingsOrderForwardBillingsListParamsOrder.DISTANCE_AMOUNT_DESC,
      sortKey: BillingsTaxiTransferredOrderListingSortKey.DISTANCE_COST_DESC,
    },
    {
      requestOrder:
        BillingsOrderForwardBillingsListParamsOrder.HALTING_AMOUNT_ASC,
      sortKey: BillingsTaxiTransferredOrderListingSortKey.STOPOVER_COST_ASC,
    },
    {
      requestOrder:
        BillingsOrderForwardBillingsListParamsOrder.HALTING_AMOUNT_DESC,
      sortKey: BillingsTaxiTransferredOrderListingSortKey.STOPOVER_COST_DESC,
    },
    {
      requestOrder:
        BillingsOrderForwardBillingsListParamsOrder.HIGHWAY_CHARGE_ASC,
      sortKey: BillingsTaxiTransferredOrderListingSortKey.TOLL_ROADS_COST_ASC,
    },
    {
      requestOrder:
        BillingsOrderForwardBillingsListParamsOrder.HIGHWAY_CHARGE_DESC,
      sortKey: BillingsTaxiTransferredOrderListingSortKey.TOLL_ROADS_COST_DESC,
    },
    {
      requestOrder:
        BillingsOrderForwardBillingsListParamsOrder.TOTAL_AMOUNT_ASC,
      sortKey: BillingsTaxiTransferredOrderListingSortKey.TOTAL_COST_ASC,
    },
    {
      requestOrder:
        BillingsOrderForwardBillingsListParamsOrder.TOTAL_AMOUNT_DESC,
      sortKey: BillingsTaxiTransferredOrderListingSortKey.TOTAL_COST_DESC,
    },
    {
      requestOrder: BillingsOrderForwardBillingsListParamsOrder.PRODUCER_ASC,
      sortKey: BillingsTaxiTransferredOrderListingSortKey.PRODUCER_ASC,
    },
    {
      requestOrder: BillingsOrderForwardBillingsListParamsOrder.PRODUCER_DESC,
      sortKey: BillingsTaxiTransferredOrderListingSortKey.PRODUCER_DESC,
    },
    {
      requestOrder: BillingsOrderForwardBillingsListParamsOrder.CONSUMER_ASC,
      sortKey: BillingsTaxiTransferredOrderListingSortKey.CONSUMER_ASC,
    },
    {
      requestOrder: BillingsOrderForwardBillingsListParamsOrder.CONSUMER_DESC,
      sortKey: BillingsTaxiTransferredOrderListingSortKey.CONSUMER_DESC,
    },
    {
      requestOrder:
        BillingsOrderForwardBillingsListParamsOrder.BILLING_MODEL_ASC,
      sortKey: BillingsTaxiTransferredOrderListingSortKey.BILLING_MODEL_ASC,
    },
    {
      requestOrder:
        BillingsOrderForwardBillingsListParamsOrder.BILLING_MODEL_DESC,
      sortKey: BillingsTaxiTransferredOrderListingSortKey.BILLING_MODEL_DESC,
    },
  ];

  return options.find((option) => option.sortKey === sortKey)?.requestOrder;
};

const createParams = (
  taxiCorporationId: string | undefined,
  page: number,
  pageSize: number,
  filters: BillingsTaxiTransferredOrderListingFilter[],
  sortKey: BillingsTaxiTransferredOrderListingSortKey | null,
): BillingsOrderForwardBillingsListParams => {
  const offset = paginationService.calculateOffset(page, pageSize);

  return {
    limit: pageSize,
    offset,
    order: createRequestOrder(sortKey),
    taxiCorporationId: taxiCorporationId,
    humanId: filters.find(
      (filter) =>
        filter.type ===
        BillingsTaxiTransferredOrderListingFilterType.INTERNAL_ORDER_ID,
    )?.value as
      | BillingsTaxiTransferredOrderListingInternalOrderIdFilter["value"]
      | undefined,
    producer: filters.find(
      (filter) =>
        filter.type ===
        BillingsTaxiTransferredOrderListingFilterType.PRODUCER_TAXI,
    )?.value as
      | BillingsTaxiTransferredOrderListingProducerTaxiFilter["value"]
      | undefined,
    consumer: filters.find(
      (filter) =>
        filter.type ===
        BillingsTaxiTransferredOrderListingFilterType.CONSUMER_TAXI,
    )?.value as
      | BillingsTaxiTransferredOrderListingConsumerTaxiFilter["value"]
      | undefined,
    startTimeSince: (
      filters.find(
        (filter) =>
          filter.type ===
          BillingsTaxiTransferredOrderListingFilterType.ORDER_START_DATE,
      )?.value as
        | BillingsTaxiTransferredOrderListingOrderStartDateFilter["value"]
        | undefined
    )?.from.toJSON(),
    startTimeTo: (
      filters.find(
        (filter) =>
          filter.type ===
          BillingsTaxiTransferredOrderListingFilterType.ORDER_START_DATE,
      )?.value as
        | BillingsTaxiTransferredOrderListingOrderStartDateFilter["value"]
        | undefined
    )?.to.toJSON(),
    status: filters.find(
      (filter) =>
        filter.type ===
        BillingsTaxiTransferredOrderListingFilterType.BILLING_STATUS,
    )?.value as
      | BillingsTaxiTransferredOrderListingStatusFilter["value"]
      | undefined,
  };
};

const billingsTaxiTransferredOrderListingRequestFactory = {
  createParams,
};

export default billingsTaxiTransferredOrderListingRequestFactory;
